import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  CircleHelp,
  FileText,
  ListChecks,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import type { ManualBodySection, ManualBodyStep } from "@arc/contracts";
import { cn } from "@/lib/utils";

/** A small glyph per step type (mirrors the player's TYPE_LABEL) so the outline reads at a glance, like a slide list. */
const TYPE_ICON: Record<ManualBodyStep["type"], LucideIcon> = {
  regular: FileText,
  question: CircleHelp,
  checklist: ListChecks,
  question_with_score: ClipboardCheck,
  show_score: Trophy,
};

/**
 * The viewer's "CONTENTS" side panel (ADR-002) — the sections → steps outline, docked next to the activity rail (the
 * rail's Contents icon flips it open). A header shows the step/section counts (slide-editor style); each **section is
 * collapsible** (chevron) and the section holding the current step auto-expands; each step carries a type glyph and the
 * active step is highlighted in the realm primary (our selected-state convention, inverts per theme). Preflight is off,
 * so the raw `<button>`/`<ul>` UA styles are reset explicitly (`border-0`, `bg-transparent`, `list-none`).
 */
export function InhaltTree({
  sections,
  currentStepId,
  onSelectStep,
}: {
  sections: ManualBodySection[];
  currentStepId: number | null;
  onSelectStep: (stepId: number) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    const cur = sections.find((s) => s.steps.some((st) => st.id === currentStepId));
    return new Set(cur ? [cur.id] : sections.length > 0 ? [sections[0]!.id] : []);
  });
  // Keep the current step's section expanded as the reader navigates (never auto-collapses the others).
  useEffect(() => {
    const cur = sections.find((s) => s.steps.some((st) => st.id === currentStepId));
    if (cur) setExpanded((prev) => (prev.has(cur.id) ? prev : new Set(prev).add(cur.id)));
  }, [currentStepId, sections]);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const stepCount = sections.reduce((n, s) => n + s.steps.length, 0);

  return (
    <nav aria-label={t("viewer.contents")} className="border-border flex w-full flex-col border-r">
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2.5">
        <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t("viewer.contentsTitle")}</span>
        <span className="text-muted-foreground text-xs tabular-nums">
          {t("viewer.countSteps", { count: stepCount })} · {t("viewer.countSections", { count: sections.length })}
        </span>
      </div>
      <ul className="flex flex-1 list-none flex-col gap-1 overflow-auto p-3">
        {sections.map((sec) => {
          const open = expanded.has(sec.id);
          return (
            <li key={sec.id}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggle(sec.id)}
                className="text-muted-foreground hover:text-foreground flex w-full cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-xs font-semibold tracking-wide uppercase"
              >
                {open ? (
                  <ChevronDown aria-hidden="true" className="size-3.5 shrink-0" />
                ) : (
                  <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
                )}
                <span className="truncate">{sec.name}</span>
              </button>
              {open ? (
                <ul className="mt-0.5 flex list-none flex-col gap-0.5 p-0 pl-3">
                  {sec.steps.map((st) => {
                    const active = st.id === currentStepId;
                    const Icon = TYPE_ICON[st.type];
                    return (
                      <li key={st.id}>
                        <button
                          type="button"
                          aria-current={active ? "step" : undefined}
                          onClick={() => onSelectStep(st.id)}
                          className={cn(
                            "flex w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm transition-colors",
                            active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted",
                          )}
                        >
                          <Icon aria-hidden="true" className={cn("size-3.5 shrink-0", active ? "" : "opacity-70")} />
                          <span className="truncate">{st.name}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
